import { motion, useReducedMotion } from "framer-motion";
import { ArrowDown, FileText } from "@phosphor-icons/react";
import { Link } from "react-router-dom";
import { useSiteConfig } from "@/hooks/useSiteConfig";
import NetworkBackground from "./NetworkBackground";
import { EASE } from "./primitives";

const MaskedLine = ({ children, delay, className = "" }) => (
  <span className={`block overflow-hidden ${className}`}>
    <motion.span
      className="block"
      initial={{ y: "112%" }}
      animate={{ y: 0 }}
      transition={{ duration: 1, delay, ease: EASE }}
    >
      {children}
    </motion.span>
  </span>
);

const Hero = () => {
  const config = useSiteConfig();
  const reduce = useReducedMotion();

  return (
    <section id="home" className="relative min-h-screen flex items-center pt-24 pb-16 overflow-hidden" data-testid="hero-section">
      <NetworkBackground />
      <div className="section-shell relative z-10 w-full">
        <motion.p
          initial={reduce ? false : { opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.15, duration: 0.8 }}
          className="font-mono text-xs sm:text-sm tracking-[0.45em] uppercase text-teal"
          data-testid="hero-eyebrow"
        >
          Jovino Coin • JCV
        </motion.p>

        <h1 className="mt-6 font-display font-bold tracking-tight leading-[1.02] text-4xl sm:text-6xl lg:text-7xl text-mist">
          <MaskedLine delay={0.25}>Built for Utility.</MaskedLine>
          <MaskedLine delay={0.4}>
            Designed for <span className="text-gold">Scarcity.</span>
          </MaskedLine>
          <MaskedLine delay={0.55}>
            Powered by <span className="text-teal">Community.</span>
          </MaskedLine>
        </h1>

        <motion.p
          initial={reduce ? false : { opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.8, duration: 0.8, ease: EASE }}
          className="mt-7 max-w-2xl text-base md:text-lg text-slate leading-relaxed"
          data-testid="hero-copy"
        >
          Jovino Coin (JCV) is a fixed-supply decentralized utility token built on the BNB Smart Chain, designed to
          support payments, staking, governance, ecosystem services, and future Web3 applications.
        </motion.p>

        <motion.div
          initial={reduce ? false : { opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1, duration: 0.9, ease: EASE }}
          className="mt-12"
        >
          <div className="gold-number font-semibold text-2xl sm:text-4xl lg:text-5xl break-all" data-testid="hero-supply-counter">
            {config.token.maxSupply}
            <span className="ml-3 text-mist/80 text-lg sm:text-2xl lg:text-3xl font-display font-semibold tracking-normal">JCV</span>
          </div>
          <div className="mt-3 font-mono text-[11px] sm:text-xs tracking-[0.35em] uppercase text-slate" data-testid="hero-supply-label">
            Fixed Maximum Supply
          </div>

          <div className="mt-7 flex flex-wrap gap-2.5" data-testid="hero-badges">
            {config.badges.map((b) => (
              <span key={b} className="rounded-full border border-teal/40 bg-teal/5 px-4 py-1.5 font-mono text-[10px] sm:text-[11px] tracking-[0.2em] text-teal">
                {b}
              </span>
            ))}
          </div>

          <div className="mt-10 flex flex-wrap items-center gap-4">
            <button
              onClick={() => window.dispatchEvent(new CustomEvent("jcv:scroll", { detail: "#presale" }))}
              data-testid="hero-cta-presale"
              className="rounded-full bg-teal px-8 py-3.5 font-display font-semibold text-navy text-sm tracking-wide transition-transform duration-300 hover:scale-[1.03] teal-glow"
            >
              Presale — 1 Nov 2026
            </button>
            <Link
              to="/whitepaper"
              data-testid="hero-cta-whitepaper"
              className="inline-flex items-center gap-2 rounded-full border border-line px-8 py-3.5 font-display font-semibold text-sm text-mist transition-colors duration-300 hover:border-teal hover:text-teal"
            >
              <FileText size={18} /> Read White Paper
            </Link>
          </div>
        </motion.div>
      </div>

      <motion.button
        onClick={() => window.dispatchEvent(new CustomEvent("jcv:scroll", { detail: "#about" }))}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.6 }}
        className="absolute bottom-6 left-1/2 -translate-x-1/2 text-slate hover:text-teal transition-colors duration-300 animate-float-slow"
        aria-label="Scroll to about section"
        data-testid="hero-scroll-cue"
      >
        <ArrowDown size={22} />
      </motion.button>
    </section>
  );
};

export default Hero;
